// =============================================================================
// MeuDriver — AdminFaq (Batch T1)
// CRUD de categorias e artigos de FAQ — /admin/faq
// =============================================================================

import { useState, useCallback } from 'react'
import { z } from 'zod'
import { Plus, Pencil, Trash2, Eye, EyeOff, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useFaqCategories,
  useAdminFaq,
  useCreateFaqArticle,
  useUpdateFaqArticle,
  useDeleteFaqArticle,
} from '@/hooks/useFaq'
import type { FaqArticle, FaqCategory } from '@/hooks/useFaq'

// ─── Schema de validação ──────────────────────────────────────────────────────

const articleSchema = z.object({
  question: z.string().min(5, 'Mínimo 5 caracteres').max(500, 'Máximo 500 caracteres'),
  answer:   z.string().min(10, 'Mínimo 10 caracteres').max(5000, 'Máximo 5000 caracteres'),
})

// ─── Modal de artigo ──────────────────────────────────────────────────────────

interface ArticleModalProps {
  open: boolean
  onClose: () => void
  categories: FaqCategory[]
  article?: FaqArticle | null
}

function ArticleModal({ open, onClose, categories, article }: ArticleModalProps) {
  const isEdit = Boolean(article)
  const createMutation = useCreateFaqArticle()
  const updateMutation = useUpdateFaqArticle()

  const [categoryId, setCategoryId] = useState(article?.category_id ?? '')
  const [question, setQuestion]   = useState(article?.question ?? '')
  const [answer, setAnswer]       = useState(article?.answer ?? '')
  const [published, setPublished] = useState(article?.is_published ?? true)
  const [order, setOrder]         = useState(String(article?.display_order ?? 0))
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // Reset ao abrir com novo artigo
  const handleOpen = useCallback(() => {
    setCategoryId(article?.category_id ?? '')
    setQuestion(article?.question ?? '')
    setAnswer(article?.answer ?? '')
    setPublished(article?.is_published ?? true)
    setOrder(String(article?.display_order ?? 0))
    setValidationErrors({})
  }, [article])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!categoryId) return

    const parsed = articleSchema.safeParse({ question: question.trim(), answer: answer.trim() })
    if (!parsed.success) {
      const errs: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        errs[issue.path[0] as string] = issue.message
      }
      setValidationErrors(errs)
      return
    }
    setValidationErrors({})

    const payload = {
      category_id:   categoryId,
      question:      parsed.data.question,
      answer:        parsed.data.answer,
      display_order: Number(order) || 0,
      is_published:  published,
    }

    if (isEdit && article) {
      updateMutation.mutate({ id: article.id, updates: payload }, { onSuccess: onClose })
    } else {
      createMutation.mutate(payload, { onSuccess: onClose })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); else handleOpen() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar artigo' : 'Novo artigo de FAQ'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label>Categoria</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="faq-category-select">
                <SelectValue placeholder="Selecionar categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Pergunta</Label>
            <Input
              value={question}
              onChange={(e) => { setQuestion(e.target.value); setValidationErrors((p) => ({ ...p, question: '' })) }}
              placeholder="Ex: Como agendar uma aula?"
              maxLength={500}
              data-testid="faq-question-input"
            />
            <div className="flex justify-between">
              {validationErrors.question
                ? <p className="text-xs text-red-600">{validationErrors.question}</p>
                : <span />}
              <p className="text-xs text-muted-foreground">{question.length}/500</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Resposta</Label>
            <Textarea
              value={answer}
              onChange={(e) => { setAnswer(e.target.value); setValidationErrors((p) => ({ ...p, answer: '' })) }}
              placeholder="Resposta detalhada..."
              rows={6}
              maxLength={5000}
              className="resize-none"
              data-testid="faq-answer-input"
            />
            <div className="flex justify-between">
              {validationErrors.answer
                ? <p className="text-xs text-red-600">{validationErrors.answer}</p>
                : <span />}
              <p className="text-xs text-muted-foreground">{answer.length}/5000</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Ordem</Label>
              <Input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                min={0}
                className="w-full"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={published ? 'published' : 'draft'} onValueChange={(v) => setPublished(v === 'published')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="draft">Rascunho</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending || !categoryId || question.trim().length < 5 || answer.trim().length < 10}>
              {isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar artigo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ─── Linha de artigo ──────────────────────────────────────────────────────────

function ArticleRow({
  article,
  categories,
  onEdit,
}: {
  article: FaqArticle
  categories: FaqCategory[]
  onEdit: (a: FaqArticle) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const deleteMutation = useDeleteFaqArticle()
  const updateMutation = useUpdateFaqArticle()

  const category = categories.find((c) => c.id === article.category_id)

  function togglePublish() {
    updateMutation.mutate({
      id: article.id,
      updates: { is_published: !article.is_published },
    })
  }

  return (
    <div className="border border-border/60 rounded-xl bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 text-left text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
        >
          {article.question}
        </button>

        <div className="flex items-center gap-1 shrink-0">
          {category && (
            <Badge variant="secondary" className="text-[10px]">{category.name}</Badge>
          )}
          {!article.is_published && (
            <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">Rascunho</Badge>
          )}

          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 text-muted-foreground"
            title="Expandir"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>

          <button
            onClick={togglePublish}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 text-muted-foreground"
            title={article.is_published ? 'Despublicar' : 'Publicar'}
          >
            {article.is_published
              ? <Eye className="h-3.5 w-3.5" />
              : <EyeOff className="h-3.5 w-3.5" />}
          </button>

          <button
            onClick={() => onEdit(article)}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 text-muted-foreground"
            title="Editar"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>

          <button
            onClick={() => {
              if (confirm('Remover este artigo?')) {
                deleteMutation.mutate(article.id)
              }
            }}
            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-muted-foreground hover:text-red-600"
            title="Excluir"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border/40 px-4 py-3 text-sm text-muted-foreground whitespace-pre-line">
          {article.answer}
        </div>
      )}
    </div>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function AdminFaq() {
  const [modalOpen, setModalOpen] = useState(false)
  const [editingArticle, setEditingArticle] = useState<FaqArticle | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('all')

  const { data: categories = [], isLoading: catsLoading } = useFaqCategories()
  const { data: articles = [], isLoading: artsLoading } = useAdminFaq()

  const filtered = filterCategory === 'all'
    ? articles
    : articles.filter((a) => a.category_id === filterCategory)

  function openCreate() {
    setEditingArticle(null)
    setModalOpen(true)
  }

  function openEdit(article: FaqArticle) {
    setEditingArticle(article)
    setModalOpen(true)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-brand-600" />
            FAQ — Base de Conhecimento
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Gerencie perguntas e respostas exibidas na central de ajuda.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="faq-create-button">
          <Plus className="h-4 w-4 mr-1.5" />
          Novo artigo
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-border/60 bg-white dark:bg-gray-900 p-4 text-center">
          <p className="text-2xl font-extrabold text-gray-900 dark:text-white">{articles.length}</p>
          <p className="text-xs text-muted-foreground">Total de artigos</p>
        </div>
        <div className="rounded-xl border border-border/60 bg-white dark:bg-gray-900 p-4 text-center">
          <p className="text-2xl font-extrabold text-green-600">{articles.filter((a) => a.is_published).length}</p>
          <p className="text-xs text-muted-foreground">Publicados</p>
        </div>
        <div className="rounded-xl border border-border/60 bg-white dark:bg-gray-900 p-4 text-center">
          <p className="text-2xl font-extrabold text-orange-500">{articles.filter((a) => !a.is_published).length}</p>
          <p className="text-xs text-muted-foreground">Rascunhos</p>
        </div>
      </div>

      {/* Filtro por categoria */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilterCategory('all')}
          className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
            filterCategory === 'all'
              ? 'bg-brand-700 text-white border-brand-700'
              : 'bg-white dark:bg-gray-900 text-muted-foreground border-border/60 hover:border-brand-400'
          }`}
        >
          Todas as categorias
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setFilterCategory(c.id)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
              filterCategory === c.id
                ? 'bg-brand-700 text-white border-brand-700'
                : 'bg-white dark:bg-gray-900 text-muted-foreground border-border/60 hover:border-brand-400'
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {/* Lista de artigos */}
      {artsLoading || catsLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Nenhum artigo encontrado. Crie o primeiro!
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <ArticleRow
              key={a.id}
              article={a}
              categories={categories}
              onEdit={openEdit}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <ArticleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        categories={categories}
        article={editingArticle}
      />
    </div>
  )
}
